import React, { useState } from "react";
import { useLang } from "../i18n/LanguageContext";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { CheckCircle2, Send, Loader2 } from "lucide-react";

const CATEGORY_KEYS = ["support", "scam_report", "billing", "privacy", "feedback", "other"];
const API = process.env.REACT_APP_BACKEND_URL;

export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", subject: "", category: "support", message: "" });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      const res = await fetch(`${API}/api/public/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || t("contact.error"));
      }
      setSent(true);
    } catch (err) {
      setError(err.message || t("contact.error"));
    } finally {
      setSending(false);
    }
  };

  const labelCls = "mb-1.5 block text-sm font-medium text-slate-600";
  const inputCls =
    "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-600 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200";

  return (
    <div className="tw-grid-bg min-h-screen pt-28 pb-24 sm:pt-32">
      <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">{t("contact.title")}</h1>
          <p className="mt-3 text-base text-slate-500">{t("contact.subtitle")}</p>
          <p className="mt-2 text-sm text-slate-500">
            {t("contact.directEmail")}{" "}
            <a href="mailto:info@oasi.be" data-testid="contact-direct-email" className="font-medium text-blue-700 transition-colors hover:text-blue-700">
              info@oasi.be
            </a>
          </p>
        </div>

        <div className="mt-10 rounded-3xl border border-slate-200 bg-white p-6 sm:p-8">
          {sent ? (
            <div data-testid="contact-success" className="flex flex-col items-center py-10 text-center">
              <CheckCircle2 className="h-12 w-12 text-emerald-400" />
              <p className="mt-4 max-w-sm text-base text-slate-700">{t("contact.success")}</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label className={labelCls}>{t("contact.name")}</label>
                  <input
                    data-testid="contact-name" required value={form.name}
                    onChange={(e) => set("name", e.target.value)}
                    placeholder={t("contact.placeholder.name")} className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>{t("contact.email")}</label>
                  <input
                    data-testid="contact-email" type="email" required value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                    placeholder={t("contact.placeholder.email")} className={inputCls}
                  />
                </div>
              </div>

              <div>
                <label className={labelCls}>{t("contact.subject")}</label>
                <input
                  data-testid="contact-subject" required value={form.subject}
                  onChange={(e) => set("subject", e.target.value)}
                  placeholder={t("contact.placeholder.subject")} className={inputCls}
                />
              </div>

              <div>
                <label className={labelCls}>{t("contact.category")}</label>
                <Select value={form.category} onValueChange={(v) => set("category", v)}>
                  <SelectTrigger data-testid="contact-category" className={inputCls}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="tw-glass border-slate-200 text-slate-700">
                    {CATEGORY_KEYS.map((k) => (
                      <SelectItem key={k} value={k} data-testid={`contact-category-${k}`}>
                        {t(`contact.categories.${k}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className={labelCls}>{t("contact.message")}</label>
                <textarea
                  data-testid="contact-message" required rows={5} value={form.message}
                  onChange={(e) => set("message", e.target.value)}
                  placeholder={t("contact.placeholder.message")} className={`${inputCls} resize-none`}
                />
              </div>

              <button
                type="submit"
                data-testid="contact-submit"
                disabled={sending}
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#1E3A8A] px-6 py-3.5 text-base font-semibold text-white transition-colors hover:bg-[#1D4ED8] disabled:opacity-60"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {t("contact.send")}
              </button>
              {error && (
                <p data-testid="contact-error" className="text-center text-sm text-red-400">{error}</p>
              )}
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
